import {
  useFileCollection,
  useFileCollectionsFromParentId,
  useFilesFromFileCollectionId,
} from '@witmetrics/data-store';
import { SOCKET_EVENTS } from '@witmetrics/utils/constants';
import { useBridge } from '@/providers/BridgeProvider';
import { useToggle } from '@/hooks/useToggle';
import { useSockets } from '@/providers/SocketsProvider';
import { useMountEffect } from '@/hooks/useMountEffect';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import FileCollection from '@/components/FileCollection';
import BridgeConfirmationDialog from '@/dialogs/BridgeConfirmationDialog';
import DownloadToaster from '@/toasters/DownloadToaster';
import { classes } from './utils';

const { BRIDGE_OPEN_FILE } = SOCKET_EVENTS;

type BridgeFilesDialogProps = {
  fileCollectionId: number;
  onClose: () => void;
};

export default function BridgeFilesDialog({
  fileCollectionId,
  onClose,
}: BridgeFilesDialogProps) {
  const fileCollection = useFileCollection(fileCollectionId);
  const childCollections = useFileCollectionsFromParentId(fileCollectionId);
  const childFiles = useFilesFromFileCollectionId(fileCollectionId);
  const { sendFileToBridge } = useBridge();
  const { subscribe, unsubscribe } = useSockets();
  const [isToasterOpen, toggleToaster] = useToggle();
  const [isConfirmationShown, toggleConfirmation] = useToggle();

  useMountEffect(() => {
    // Listen for when the file's been opened in Bridge
    subscribe({
      endpoint: BRIDGE_OPEN_FILE,
      callback: () => {
        // Delay closing a bit to avoid flicker
        setTimeout(() => toggleConfirmation(false), 3000);
      },
    });
    return () => {
      unsubscribe(BRIDGE_OPEN_FILE);
    };
  });

  const handleOpenFile = async (fileId: number) => {
    // TODO: Can we pass a fileVersionId into this instead of a fileId?
    try {
      toggleToaster(true);
      toggleConfirmation(true);
      await sendFileToBridge(fileId);
      toggleToaster(false);
    } catch (err) {
      throw err; // TODO
    }
  };

  return (
    <>
      {isConfirmationShown && (
        <BridgeConfirmationDialog onClose={() => toggleConfirmation(false)} />
      )}
      <DownloadToaster
        open={isToasterOpen}
        isLoading={true}
        message="Opening file..."
        successMessage="File opened"
        onClose={() => toggleToaster(false)}
      />
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle
          title="Send to Printer"
          subtitle="Select a file to open with witMetrics Bridge"
          onClose={onClose}
        />
        <DialogBody className={classes.body}>
          {fileCollection && (
            <FileCollection
              defaultExpanded
              collectionView="LIST"
              fileCollections={childCollections}
              files={childFiles}
              onViewFileCollection={() => null}
              onViewFile={handleOpenFile}
            />
          )}
        </DialogBody>
      </Dialog>
    </>
  );
}
